import {
  User,
  Accessibility,
  ChevronDown,
  ChevronRight,
  Check,
  Archive,
  Trash2,
  Loader2,
  AlertTriangle,
  ExternalLink,
  Upload,
} from 'lucide-react';
import { useRef, useState, type ChangeEvent } from 'react';
import { api } from '../../../lib/api.js';
import { fileToBase64 } from '../hooks/useCreateApprenant.js';
import { useApprenant, useApprenantMutation } from '../hooks/useApprenant.js';
import { useApprenantActions, type ValidateResult } from '../hooks/useApprenantActions.js';
import { useFormateursList, useEntreprisesList, useFormationsList } from '../hooks/useLookups.js';
import { EditableField } from '../../../components/EditableField.js';
import { SearchableLookup } from '../../../components/SearchableLookup.js';
import { ValidationReceiptModal } from '../components/ValidationReceiptModal.js';
import { ValidationProgressModal } from '../components/ValidationProgressModal.js';

interface Props {
  apprenantId: string;
}

// ─── Picklists Zoho ──────────────────────────────────────────────────
const PICKLIST_CIVILITE = [
  { value: 'M.', label: 'M.' },
  { value: 'Mme', label: 'Mme' },
  { value: 'Mx', label: 'Mx' },
];
const PICKLIST_LIEU = [
  { value: 'Visio', label: 'Visio' },
  { value: 'Présentiel', label: 'Présentiel' },
  { value: 'E-learning', label: 'E-learning' },
  { value: 'Hybride', label: 'Hybride' },
];
// PICKLIST_STATUT retiré : statut pipeline géré dans la TimelineCard (pleine largeur)
const PICKLIST_FINANCEMENT = [
  { value: 'CPF', label: 'CPF' },
  { value: 'Autofinancement', label: 'Autofinancement' },
  { value: 'OPCO', label: 'OPCO' },
  { value: 'Sous-traitance', label: 'Sous-traitance' },
];
const PICKLIST_SEGMENT = [
  { value: 'CPF (State Funded)', label: 'CPF (State Funded)' },
  { value: 'Pro Non-CPF (Corporate)', label: 'Pro Non-CPF (Corporate)' },
  { value: 'Andalous e-Learning (Private)', label: 'Andalous e-Learning (Private)' },
];
const PICKLIST_SITUATION = [
  { value: 'Salarié', label: 'Salarié' },
  { value: "demandeur d'emploi", label: "Demandeur d'emploi" },
  { value: 'étudiant', label: 'Étudiant' },
  { value: 'N/R', label: 'Non renseigné' },
];
const PICKLIST_SOURCE = [
  { value: 'Manuel', label: 'Manuel' },
  { value: 'Moodle auto-sync', label: 'Moodle auto-sync' },
  { value: 'Import', label: 'Import EDOF' },
  { value: 'Formulaire web', label: 'Formulaire web' },
  { value: 'Validé manuellement', label: 'Validé manuellement' },
];

/**
 * Slice IdentiteCard — Template final (28/05/2026)
 *
 * Sources conventionnées :
 *  📄 = extrait du PDF EDOF (icône sur le champ)
 *  🗂️ = lookup vers Zoho (Formation, Formateur, Entreprise)
 *  ✍️ = saisie manuelle (défaut)
 *  🔁 = dérivé d'un autre champ
 *  🧮 = calcul auto (read-only)
 *
 * Règle absolue : TOUS les champs sont éditables.
 * Footer : 3 actions — Valider l'identité / Archiver / Supprimer.
 */
export function IdentiteCard({ apprenantId }: Props) {
  const { data: a, isLoading, error } = useApprenant(apprenantId);
  const mutate = useApprenantMutation(apprenantId);
  const { validate, archive, remove, isPending } = useApprenantActions(apprenantId);
  const { items: formations } = useFormationsList();
  const { items: formateurs } = useFormateursList();
  const { items: entreprises } = useEntreprisesList();

  const isRealApi = a?.source === 'api';

  // Sections collapsibles
  const [openSections, setOpenSections] = useState({
    coordonnees: true,
    formation: true,
    financement: true,
    acteurs: true,
    administratif: false,
  });
  const toggle = (k: keyof typeof openSections) => setOpenSections((s) => ({ ...s, [k]: !s[k] }));

  // Actions states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [validateResult, setValidateResult] = useState<ValidateResult | null>(null);
  // PDF EDOF import après création (apprenant créé manuellement ou par lead)
  const pdfFileInput = useRef<HTMLInputElement>(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  // Progress UX : ouvre le modal IMMÉDIATEMENT au click avec étapes en pending,
  // simule l'avancée pendant que la requête HTTP tourne (~30-60s), bascule en
  // résultats réels à la réponse. Évite la frustration "j'ai cliqué, rien ne se passe".
  const [validateInProgress, setValidateInProgress] = useState(false);
  const [actionResult, setActionResult] = useState<{ type: 'ok' | 'err'; msg: string } | null>(
    null,
  );

  if (isLoading)
    return (
      <Frame title="Identité">
        <Skel />
      </Frame>
    );
  if (error || !a)
    return (
      <Frame title="Identité">
        <Err msg={error?.message ?? 'Aucune donnée'} />
      </Frame>
    );

  const edit = isRealApi
    ? <K extends keyof Parameters<typeof mutate>[0]>(key: K) =>
        async (v: string) => {
          await mutate({ [key]: v } as Parameters<typeof mutate>[0]);
        }
    : () => undefined;
  const editBool = isRealApi
    ? <K extends keyof Parameters<typeof mutate>[0]>(key: K, val: boolean) =>
        async () => {
          await mutate({ [key]: val } as Parameters<typeof mutate>[0]);
        }
    : () => undefined;

  const totalHeuresCalcule = (a.heuresSynchrones ?? 0) + (a.heuresAsynchrones ?? 0);
  const heuresPdfMismatch =
    a.heuresGlobales !== null && totalHeuresCalcule > 0 && totalHeuresCalcule !== a.heuresGlobales;

  const handleValidate = async () => {
    setActionResult(null);
    setValidateResult(null);
    setValidateInProgress(true); // ouvre IMMÉDIATEMENT le modal de progression
    try {
      const r = await validate();
      // Réponse reçue → bascule en modal résultats
      setValidateInProgress(false);
      setValidateResult(r);
    } catch (e) {
      setValidateInProgress(false);
      setActionResult({ type: 'err', msg: (e as Error).message });
    }
  };
  const handleArchive = async () => {
    if (!confirm("Archiver cet apprenant ? Son statut passera à 'Abandonné'.")) return;
    setActionResult(null);
    try {
      await archive();
      setActionResult({ type: 'ok', msg: '✓ Apprenant archivé (statut Abandonné)' });
    } catch (e) {
      setActionResult({ type: 'err', msg: (e as Error).message });
    }
  };
  /**
   * Import manuel d'un PDF EDOF après création.
   * Cas d'usage : apprenant créé via onglet Manuel/Lead (sans PDF), Rafik
   * récupère le PDF CPF plus tard et veut l'attacher pour que le prochain
   * "Valider l'identité" l'upload dans Drive/01-Convention.
   */
  const handleAttachPdf = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setActionResult({ type: 'err', msg: 'Seuls les PDF sont acceptés.' });
      return;
    }
    setActionResult(null);
    setPdfUploading(true);
    try {
      const pdfBase64 = await fileToBase64(file);
      const r = await api.post<{ id: string; sizeBytes: number; message: string }>(
        `/api/v1/v2/apprenants/${apprenantId}/attach-pdf`,
        { pdfBase64 },
      );
      setActionResult({
        type: 'ok',
        msg: `✓ ${r.message} (${Math.round(r.sizeBytes / 1024)} KB)`,
      });
    } catch (err) {
      setActionResult({ type: 'err', msg: (err as Error).message });
    } finally {
      setPdfUploading(false);
      if (pdfFileInput.current) pdfFileInput.current.value = ''; // reset pour ré-upload possible
    }
  };

  const handleDelete = async () => {
    setActionResult(null);
    try {
      await remove();
      setActionResult({ type: 'ok', msg: '✓ Apprenant supprimé définitivement' });
      setShowDeleteConfirm(false);
      setTimeout(() => {
        window.location.href = '/apprenants/demo-martine';
      }, 1500);
    } catch (e) {
      setActionResult({ type: 'err', msg: (e as Error).message });
    }
  };

  return (
    <Frame
      title="Identité"
      icon={<User className="text-primary-500 h-4 w-4" />}
      badge={
        <div className="flex items-center gap-1.5">
          {a.handicap && (
            <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
              <Accessibility className="h-3 w-3" />
              PSH
            </span>
          )}
          {a.complianceFlag?.startsWith('Green') && (
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
              ✓ Validé
            </span>
          )}
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
              a.statut === 'actif'
                ? 'bg-emerald-50 text-emerald-700'
                : a.statut === 'abandon'
                  ? 'bg-rose-50 text-rose-700'
                  : 'bg-neutral-100 text-neutral-600'
            }`}
          >
            {a.statut === 'actif' ? `Actif · ${a.pipelineStage}` : a.statut}
          </span>
        </div>
      }
    >
      {/* En-tête nom + dossier */}
      <div className="mb-3 flex items-baseline justify-between border-b border-neutral-100 pb-2">
        <div>
          <div className="text-base font-semibold text-neutral-900">
            {a.civilite && <span className="mr-1 text-neutral-500">{a.civilite}</span>}
            {a.prenom} {a.nom}
          </div>
          {a.numeroDossier && (
            <div className="text-[10px] text-neutral-500">Dossier {a.numeroDossier}</div>
          )}
        </div>
      </div>

      {/* ── COORDONNÉES ── */}
      <Section
        title="Coordonnées"
        open={openSections.coordonnees}
        onToggle={() => toggle('coordonnees')}
      >
        <EditableField
          label="Civilité"
          value={a.civilite}
          options={PICKLIST_CIVILITE}
          onSave={edit('civilite')}
        />
        <EditableField label="Prénom" value={a.prenom} source="pdf" onSave={edit('prenom')} />
        <EditableField label="Nom" value={a.nom} source="pdf" onSave={edit('nom')} />
        <EditableField
          label="Email principal"
          value={a.email}
          type="email"
          source="pdf"
          onSave={edit('email')}
        />
        <EditableField
          label="Email secondaire"
          value={a.emailSecondaire}
          type="email"
          onSave={edit('emailSecondaire')}
        />
        <EditableField
          label="Téléphone"
          value={a.telephone}
          type="tel"
          source="pdf"
          onSave={edit('telephone')}
        />
        <EditableField
          label="Date de naissance"
          value={a.dateNaissance}
          type="date"
          onSave={edit('dateNaissance')}
        />
        <EditableField
          label="Situation pro"
          value={a.situationProfessionnelle}
          options={PICKLIST_SITUATION}
          onSave={edit('situationProfessionnelle')}
        />
        <div className="col-span-2">
          <EditableField
            label="Adresse complète"
            value={a.adresse}
            source="pdf"
            onSave={edit('adresse')}
          />
        </div>
      </Section>

      {/* ── FORMATION ── */}
      <Section title="Formation" open={openSections.formation} onToggle={() => toggle('formation')}>
        {/* N° dossier CPF en haut (sur 2 colonnes pour visibilité) */}
        <div className="col-span-2">
          <EditableField
            label="N° dossier CPF"
            value={a.numeroDossier}
            source="pdf"
            onSave={edit('numeroDossier')}
          />
        </div>
        {/* Intitulé commercial : depuis Formation.nomCommercial (=PDF/EDOF) — read-only */}
        <div className="col-span-2">
          <EditableField
            label="Intitulé commercial (mis en avant — PDF/EDOF)"
            value={a.formation?.nomCommercial ?? null}
            source="pdf"
            readOnly
          />
        </div>
        {/* Intitulé interne : 🗂️ lookup vers Formation Zoho (= code interne RK) */}
        <div className="col-span-2">
          <LookupField
            label="Intitulé interne (OF — code catalogue)"
            value={a.formation?.id ?? null}
            items={formations}
            source="lookup"
            onSelect={
              isRealApi ? async (id) => void (await mutate({ formationId: id ?? '' })) : undefined
            }
          />
        </div>
        {/* Date entrée + Date sortie côte à côte */}
        <EditableField
          label="Date d'entrée"
          value={a.dateInscription}
          type="date"
          source="pdf"
          onSave={edit('dateEntree')}
        />
        <EditableField
          label="Date de sortie"
          value={a.dateSortie}
          type="date"
          source="pdf"
          onSave={edit('dateSortie')}
        />
        {/* Lieu sur 2 colonnes */}
        <div className="col-span-2">
          <EditableField
            label="Lieu / Modalité"
            value={a.lieuFormation}
            options={PICKLIST_LIEU}
            onSave={edit('lieuFormation')}
          />
        </div>
        {/* Heures : volume PDF + total calculé sur 1ère ligne, sync/async sur 2ème */}
        <EditableField
          label="Volume global heures (PDF)"
          value={a.heuresGlobales}
          type="number"
          source="pdf"
          onSave={edit('nombreHeuresGlobal')}
        />
        <EditableField
          label={`Total calculé (sync + async)${heuresPdfMismatch ? ' ⚠' : ''}`}
          value={totalHeuresCalcule || null}
          type="number"
          source="computed"
          readOnly
        />
        <EditableField
          label="Heures sync (visio)"
          value={a.heuresSynchrones}
          type="number"
          onSave={edit('heuresSynchrones')}
        />
        <EditableField
          label="Heures async (Moodle)"
          value={a.heuresAsynchrones}
          type="number"
          onSave={edit('heuresAsynchrones')}
        />
        {heuresPdfMismatch && (
          <div className="col-span-2 flex items-center gap-1 rounded bg-amber-50 px-2 py-1 text-[10px] text-amber-700">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            Total calculé ({totalHeuresCalcule}h) ≠ volume PDF ({a.heuresGlobales}h) — vérifie la
            répartition sync/async.
          </div>
        )}
      </Section>

      {/* ── FINANCEMENT ── */}
      <Section
        title="Financement"
        open={openSections.financement}
        onToggle={() => toggle('financement')}
      >
        <EditableField
          label="Type"
          value={a.typeFinancement}
          highlight="primary"
          options={PICKLIST_FINANCEMENT}
          onSave={edit('typeFinancement')}
        />
        <EditableField
          label="Segment marché"
          value={a.activityVertical}
          source="auto"
          options={PICKLIST_SEGMENT}
          onSave={edit('activityVertical')}
        />
        <BoolField
          label="Élève CPF"
          value={a.eleveCPF}
          source="auto"
          onToggle={editBool('eleveCpf', !a.eleveCPF)}
        />
        <BoolField
          label="BPF éligible"
          value={a.bpfEligible}
          onToggle={editBool('bpfEligible', !a.bpfEligible)}
        />
        <BoolField
          label="Public PSH (handicap)"
          value={a.handicap}
          onToggle={editBool('handicap', !a.handicap)}
        />
      </Section>

      {/* ── ACTEURS (lookups réels) ── */}
      <Section title="Acteurs" open={openSections.acteurs} onToggle={() => toggle('acteurs')}>
        <LookupField
          label="Formateur assigné"
          value={a.formateur?.id ?? null}
          items={formateurs}
          source="lookup"
          highlight="violet"
          onSelect={
            isRealApi ? async (id) => void (await mutate({ formateurId: id ?? '' })) : undefined
          }
        />
        <LookupField
          label="Entreprise (si OPCO/ST)"
          value={a.entreprise?.id ?? null}
          items={entreprises}
          source="lookup"
          onSelect={
            isRealApi ? async (id) => void (await mutate({ entrepriseId: id ?? '' })) : undefined
          }
        />
        {/* OF Expéditeur — dérivé du segment, read-only */}
        <div className="col-span-2">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
            OF expéditeur (auto-déterminé par le segment)
          </div>
          <div
            className={`mt-1 inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 ${
              a.ofSender?.scope === 'andalous'
                ? 'border-violet-200 bg-violet-50'
                : 'border-sky-200 bg-sky-50'
            }`}
          >
            <span
              className={`text-[10px] font-bold uppercase tracking-wider ${
                a.ofSender?.scope === 'andalous' ? 'text-violet-700' : 'text-sky-700'
              }`}
            >
              {a.ofSender?.scope === 'andalous' ? '🎓 Andalous' : '📚 RK Formations'}
            </span>
            <span className="text-xs font-medium text-neutral-800">{a.ofSender?.name}</span>
            <span className="text-[11px] text-neutral-600">·</span>
            <a
              href={`mailto:${a.ofSender?.email}`}
              className="text-primary-600 text-[11px] font-medium hover:underline"
            >
              {a.ofSender?.email}
            </a>
          </div>
          <div className="mt-1 text-[10px] italic text-neutral-500">
            🔁 Change auto si tu modifies le segment dans Financement → CPF/OPCO/Corporate → RK ·
            Andalous Private → Andalous
          </div>
        </div>
      </Section>

      {/* ── ADMINISTRATIF ── */}
      <Section
        title="Administratif"
        open={openSections.administratif}
        onToggle={() => toggle('administratif')}
      >
        <EditableField
          label="Source inscription"
          value={a.source}
          source="auto"
          options={PICKLIST_SOURCE}
          onSave={edit('source')}
        />
        <EditableField
          label="Moodle User ID"
          value={a.moodleUserId}
          source="lookup"
          onSave={edit('moodleUserId')}
        />
        <div className="col-span-2">
          <EditableField
            label="Commentaire d'entrée de formation (note OF)"
            value={a.commentaires}
            onSave={edit('commentaires')}
          />
        </div>
      </Section>

      {/* ── FOOTER ACTIONS ── */}
      <div className="mt-4 border-t border-neutral-200 pt-3">
        {actionResult && (
          <div
            className={`mb-2 rounded p-2 text-xs ${
              actionResult.type === 'ok'
                ? 'bg-emerald-50 text-emerald-800'
                : 'bg-rose-50 text-rose-800'
            }`}
          >
            {actionResult.msg}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleValidate}
            disabled={!isRealApi || isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            Valider l'identité
            <ExternalLink className="h-3 w-3 opacity-70" />
          </button>
          {/* Bouton import PDF EDOF — utile quand fiche créée manuellement
              ou par lead (pas via onglet PDF). Au prochain Valider, le PDF
              sera uploadé dans Drive/01-Convention. */}
          <button
            type="button"
            onClick={() => pdfFileInput.current?.click()}
            disabled={!isRealApi || isPending || pdfUploading}
            title="Joindre un PDF EDOF qui sera uploadé dans Drive au prochain Valider"
            className="inline-flex items-center gap-1.5 rounded-lg bg-sky-100 px-3 py-1.5 text-xs font-semibold text-sky-800 hover:bg-sky-200 disabled:opacity-50"
          >
            {pdfUploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            Importer PDF EDOF
          </button>
          <input
            ref={pdfFileInput}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleAttachPdf}
            className="hidden"
          />
          <button
            type="button"
            onClick={handleArchive}
            disabled={!isRealApi || isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-200 disabled:opacity-50"
          >
            <Archive className="h-3.5 w-3.5" />
            Archiver
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={!isRealApi || isPending}
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Supprimer
          </button>
        </div>
        <div className="mt-1.5 text-[10px] text-neutral-500">
          Valider crée le dossier Drive + les 6 docs · Importer PDF EDOF attache un PDF qui sera
          uploadé au prochain Valider · Archiver = statut Abandonné · Supprimer = Zoho définitif
        </div>
      </div>

      {/* Modal confirmation suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="max-w-md rounded-lg bg-white p-5 shadow-2xl">
            <div className="mb-3 flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" />
              <h3 className="font-bold">Supprimer définitivement ?</h3>
            </div>
            <p className="mb-4 text-sm text-neutral-700">
              Tu vas supprimer{' '}
              <strong>
                {a.prenom} {a.nom}
              </strong>{' '}
              de Zoho. <strong className="text-rose-700">Cette action est irréversible.</strong>
              {!a.nom.startsWith('[TEST]') && (
                <span className="mt-2 block rounded bg-amber-50 p-2 text-xs text-amber-800">
                  ⚠ Cette fiche n'est PAS marquée [TEST]. Es-tu sûr ?
                </span>
              )}
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isPending}
                className="inline-flex items-center gap-1 rounded bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                Oui, supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {!isRealApi && (
        <div className="mt-3 rounded bg-amber-50 px-2 py-1 text-[10px] text-amber-700">
          ⚠ Mode mock — actions désactivées. Connecte-toi pour activer.
        </div>
      )}

      {/* Modal progression IMMÉDIAT au click — étapes en checklist live */}
      <ValidationProgressModal open={validateInProgress} />

      {/* Modal récap après validation — bascule depuis ProgressModal à la réponse */}
      {validateResult && (
        <ValidationReceiptModal result={validateResult} onClose={() => setValidateResult(null)} />
      )}
    </Frame>
  );
}

// ────────────────────────────────────────────────────────────────────

function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2 rounded-md border border-neutral-100">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 bg-neutral-50 px-2 py-1.5 text-left hover:bg-neutral-100"
      >
        <span className="text-primary-700 text-[10px] font-bold uppercase tracking-widest">
          {title}
        </span>
        {open ? (
          <ChevronDown className="h-3 w-3 text-neutral-400" />
        ) : (
          <ChevronRight className="h-3 w-3 text-neutral-400" />
        )}
      </button>
      {open && <div className="grid grid-cols-2 gap-x-3 gap-y-2 px-2 py-2 text-xs">{children}</div>}
    </div>
  );
}

function LookupField({
  label,
  value,
  items,
  source,
  highlight,
  onSelect,
}: {
  label: string;
  value: string | null;
  items: { id: string; label: string; sublabel?: string }[];
  source?: 'lookup';
  highlight?: 'violet';
  onSelect?: (id: string | null) => Promise<void>;
}) {
  const isEditable = !!onSelect;
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.id === value);
  return (
    <div className="flex flex-col">
      <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-neutral-400">
        {label}
        {source === 'lookup' && (
          <span
            className="inline-flex items-center rounded-sm bg-indigo-50 px-0.5 py-px text-indigo-600"
            title="Tiré de la base Zoho (lookup)"
          >
            <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C7 2 3 4 3 6.5V18c0 2.5 4 4 9 4s9-1.5 9-4V6.5C21 4 17 2 12 2z" />
            </svg>
          </span>
        )}
      </span>
      {!open && (
        <div className="group flex items-center gap-1">
          <span
            className={`flex-1 truncate ${
              highlight === 'violet'
                ? 'inline-block rounded bg-violet-50 px-1.5 py-0.5 font-semibold text-violet-700'
                : 'font-medium text-neutral-800'
            }`}
          >
            {selected?.label ?? '—'}
          </span>
          {isEditable && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="text-primary-600 text-[10px] opacity-0 hover:underline group-hover:opacity-100"
            >
              Modifier
            </button>
          )}
        </div>
      )}
      {open && isEditable && (
        <div className="space-y-1">
          <SearchableLookup
            items={items}
            value={value}
            onSelect={async (id) => {
              await onSelect(id);
              setOpen(false);
            }}
            placeholder={`Rechercher parmi ${items.length}…`}
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[10px] text-neutral-500 hover:text-rose-600"
          >
            Annuler
          </button>
        </div>
      )}
    </div>
  );
}

function BoolField({
  label,
  value,
  source,
  onToggle,
}: {
  label: string;
  value: boolean;
  source?: 'auto';
  onToggle?: () => Promise<void>;
}) {
  const isEditable = !!onToggle;
  return (
    <div className="group flex flex-col">
      <span className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-neutral-400">
        {label}
        {source === 'auto' && (
          <span
            className="inline-flex items-center rounded-sm bg-violet-50 px-0.5 py-px text-violet-600"
            title="Dérivé d'un autre champ"
          >
            ⟲
          </span>
        )}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={isEditable ? () => void onToggle?.() : undefined}
          disabled={!isEditable}
          className={`inline-flex h-4 w-7 items-center rounded-full transition-colors ${
            value ? 'bg-emerald-500' : 'bg-neutral-300'
          } ${isEditable ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed'}`}
        >
          <span
            className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
              value ? 'translate-x-3.5' : 'translate-x-0.5'
            }`}
          />
        </button>
        <span className="text-xs text-neutral-700">{value ? 'Oui' : 'Non'}</span>
      </div>
    </div>
  );
}

function Frame({
  title,
  icon,
  badge,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
          {icon}
          {title}
        </div>
        {badge}
      </div>
      {children}
    </div>
  );
}

function Skel() {
  return <div className="h-32 animate-pulse rounded bg-neutral-100" />;
}

function Err({ msg }: { msg: string }) {
  return (
    <div className="rounded bg-rose-50 p-3 text-sm text-rose-700">Erreur de chargement : {msg}</div>
  );
}
